import {
   FormControl,
   Box,
   FormLabel,
   Flex,
   Text,
   FormErrorMessage,
   useColorModeValue,
   Button,
   useDisclosure,
   Image,
   Input,
   Modal,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   ModalHeader,
   ModalContent,
   ModalOverlay,
   AlertDialog,
   AlertDialogBody,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogContent,
   AlertDialogOverlay,
   Icon,
   ScaleFade,
} from '@chakra-ui/react';
import Select, { components } from 'react-select';
import React, { useState, forwardRef, useEffect, useRef, useMemo, useCallback, useImperativeHandle } from 'react';
import { VariableSizeList as List } from 'react-window';
import { useGlobal } from 'contexts';
import { useDispatch, useSelector } from 'react-redux';
import AddEditForm from 'views/admin/masters/Pages/CustomerMaster/AddEditForm';
import { InfoIcon, CheckCircleIcon, CloseIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';

// Icons
import { EditIcon } from '@chakra-ui/icons';

// image
import Account from '../../assets/img/nfts/Account.svg';
import Typing from '../../../src/assets/img/nfts/Typing.svg';
import NoData from '../../../src/assets/img/nfts/no data.svg';
import { getCompanyDetailsByGst } from '../../redux/actions/company';
import { addAccount } from '../../redux/actions/account';
import { removeDetails } from '../../redux/actions/company';
import { useUser } from 'contexts';

const VirtualizedMenuList = ({ options, children, maxHeight, getValue, selectOption, focusedOption }) => {
   const height = Math.min(maxHeight || 300, options.length * 35);

   const listRef = useRef(null);

   const itemHeightsRef = useRef({});

   const getItemSize = (index) => {
      return itemHeightsRef.current[index] || 55; // fallback default
   };

   useEffect(() => {
      if (focusedOption && listRef.current) {
         const index = options.findIndex((option) => option.value === focusedOption.value);
         if (index >= 0) {
            listRef.current.scrollToItem(index, 'smart');
         }
      }
   }, [focusedOption, options]);
   const bgcolor = useColorModeValue('#ffffff', '#101f4a');

   const Row = ({ index, style }) => {
      const option = options[index];
      const isSelected = getValue().some((selected) => selected.value === option.value);
      const isFocused = focusedOption?.value === option.value;

      useEffect(() => {
         const timer = setTimeout(() => {
            if (listRef.current) {
               listRef.current.resetAfterIndex(0, true); // ✅ full remeasure
            }
         }, 1); // slight delay to allow initial rendering

         return () => clearTimeout(timer);
      }, [options]);

      const refCallback = (node) => {
         if (node) {
            const height = node.getBoundingClientRect().height;
            if (itemHeightsRef.current[index] !== height) {
               itemHeightsRef.current[index] = height;

               // ✅ Only reset if listRef is ready
               if (listRef.current) {
                  listRef.current.resetAfterIndex(index);
               }
            }
         }
      };

      return (
         <div style={style}>
            <div ref={refCallback}>
               {React.cloneElement(children[index], {
                  key: option.value,
                  isSelected,
                  isFocused,
                  innerProps: {
                     onClick: () => selectOption(option),
                     onMouseEnter: () => {},
                  },
               })}
            </div>
         </div>
      );
   };

   return (
      <List
         ref={listRef}
         height={height}
         itemCount={options.length}
         itemSize={getItemSize}
         overscanCount={5}
         backgroundColor={bgcolor}>
         {Row}
      </List>
   );
};

const OptimizedOption = ({ data, innerRef, innerProps, isFocused, isSelected, selectProps, isMulti }) => {
   const handleIconClick = useCallback(
      (e) => {
         e.stopPropagation();
         selectProps.handleEdit?.(data);
      },
      [data, selectProps],
   );
   const bgcolor = useColorModeValue('#ffffff', '#101f4a');
   const namecolor = useColorModeValue('#000000', '#ffffff');

   const backgroundColor = useMemo(() => {
      if (isSelected && !isMulti) return '#3182ce';
      if (isFocused) return isSelected ? '#3182ce' : '#ebf8ff';
      return bgcolor;
   }, [isSelected, isFocused, isMulti]);

   const textColor = isSelected && !isMulti ? 'white' : 'black';
   const { user } = useUser();

   const comID = user?.loginhistory?.companyId;
   const baldata = data?.amt;

   return (
      <>
         <Box
            ref={innerRef}
            {...innerProps}
            bg={backgroundColor}
            color={textColor}
            px={3}
            py={2}
            cursor="pointer"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            textColor={isSelected ? '#ffffff' : isFocused ? '#000000' : namecolor}
            // minHeight="35px"
         >
            {/* <Flex justify="space-between" alignItems="center"> */}
            <Flex direction="column" height="auto">
               <Flex alignItems="center">
                  <Text fontWeight="medium">{data.name}</Text>
               </Flex>
               {(data.gstRegNo || data.panNo || data.mobile) && (
                  <Text fontSize="sm">
                     {[
                        data?.gstRegNo && `GST: ${data.gstRegNo}`,
                        data?.panNo && `PAN: ${data.panNo}`,
                        data?.mobile && `Mobile: ${data.mobile}`,
                     ]
                        .filter(Boolean)
                        .join(' | ')}
                  </Text>
               )}

               {baldata !== undefined && baldata !== 0 && (
                  <Text fontSize="sm" color={baldata > 0 ? 'red.400' : 'green.400'}>
                     <strong>Balance:</strong>{' '}
                     {(() => {
                        const formatted = Math.abs(baldata).toLocaleString('en-IN', {
                           style: 'currency',
                           currency: 'INR',
                        });
                        const parts = formatted.split('.');
                        if (parts.length === 2) {
                           return (
                              <>
                                 {parts[0]}.
                                 <Text as="span" fontSize="xs" verticalAlign="baseline">
                                    {parts[1]}
                                 </Text>
                              </>
                           );
                        } else {
                           return formatted;
                        }
                     })()}{' '}
                     {baldata > 0 ? 'CR' : 'DB'}
                  </Text>
               )}
               {data.ownerName && (
                  <Text fontSize="sm" color={textColor}>
                     <strong>Owner Name:</strong> {data.ownerName}
                  </Text>
               )}
            </Flex>
            {!user.isExpired && <EditIcon style={{ cursor: 'pointer' }} onClick={handleIconClick} />}
         </Box>
      </>
   );
};

// Custom Value Container for multi-select display optimization
const CustomValueContainer = ({ children, getValue, selectProps, ...props }) => {
   const selected = getValue();

   const handleClick = useCallback(() => {
      selectProps.setMenuIsOpen?.(true);
   }, [selectProps]);

   const displayText = useMemo(() => {
      if (!selectProps.isMulti || selected.length <= 1) {
         return children;
      }

      const allSelected = selected.length === selectProps.totalOptions;
      if (allSelected) {
         return 'All Selected';
      }

      const firstLabel = selected[0]?.label || '';
      const extraCount = selected.length - 1;
      return `${firstLabel} + ${extraCount} more`;
   }, [selected, selectProps.isMulti, selectProps.totalOptions, children]);

   if (selectProps.isMulti && selected.length > 1) {
      return (
         <components.ValueContainer {...props}>
            <div onClick={handleClick} style={{ cursor: 'pointer', width: '100%' }}>
               {displayText}
            </div>
         </components.ValueContainer>
      );
   }

   return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
};

// Custom No Options Message
const CustomNoOptionsMessage = ({ selectProps, children, ...props }) => {
   const { user } = useUser();
   const inputValue = selectProps.inputValue || '';
   const dispatch = useDispatch();

   const isFullGST = useMemo(
      () => (input) => {
         const gstRegex = /^[0-9]{0,2}[A-Z]{0,5}[0-9]{0,4}[A-Z]{0,1}[1-9A-Z]{0,1}Z{0,1}[0-9A-Z]{0,1}$/;
         return gstRegex.test(input);
      },
      [],
   );

   const isValidGST = useMemo(
      () => (gst) => {
         const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
         return gstRegex.test(gst);
      },
      [],
   );

   const isValidGst = useMemo(() => isValidGST(inputValue), [inputValue]);
   const isGSTSearch = useMemo(() => isFullGST(inputValue.toUpperCase()), [inputValue]);
   const bgcolor = useColorModeValue('#ffffff', '#101f4a');

   useEffect(() => {
      if (inputValue.length === 15 && isValidGst && children.length === 10) {
         // Trigger some action
         selectProps.onOpenModal?.();
         dispatch(getCompanyDetailsByGst(inputValue));
      }
   }, [children.length, dispatch, inputValue, isValidGst, selectProps]);

   const handleAddNew = useCallback(
      (e) => {
         e.stopPropagation();
         e.preventDefault();
         selectProps.onAddNew(inputValue);
      },
      [inputValue, selectProps],
   );

   if (isGSTSearch && inputValue.length < 15 && inputValue.length > 10) {
      return (
         <components.NoOptionsMessage {...props}>
            <Flex direction="column" align="center" p={2} backgroundColor={bgcolor}>
               <Image src={Typing} alt="No Data" boxSize="150px" mb={2} />
               <Text>Enter full GST number</Text>
            </Flex>
         </components.NoOptionsMessage>
      );
   }
   //    if (inputValue && children.length === 0) {
   return (
      <components.NoOptionsMessage {...props}>
         <Box width="100%" textAlign="center" py={4} backgroundColor={bgcolor}>
            <Text color="gray.500" mb={2}>
               No options found
            </Text>
            {selectProps.showCustomNoOptions && !user.isExpired && (
               <Button
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => {
                     setTimeout(() => {
                        selectProps.onAddNew(inputValue);
                     }, 0); // Let dropdown fully close before opening modal
                  }}
                  // onMouseDown={handleAddNew}
               >
                  Add "{inputValue}"
               </Button>
            )}
         </Box>
      </components.NoOptionsMessage>
   );
   //    }
   //return <components.NoOptionsMessage {...props}>{children}</components.NoOptionsMessage>;
};

// Optimized search/filter function
const filterOptions = (options, inputValue) => {
   if (!inputValue) return options;

   const searchValue = inputValue.trim();
   const filtered = options.filter(
      (option) =>
         option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
         option.value.toString().toLowerCase().includes(searchValue.toLowerCase()) ||
         option.gstRegNo?.toLowerCase().includes(searchValue.toLowerCase()) ||
         option.email?.toLowerCase().includes(searchValue.toLowerCase()) ||
         option.panNo?.toLowerCase().includes(searchValue.toLowerCase()) ||
         option.mobile?.toLowerCase().includes(searchValue.toLowerCase()) ||
         option.ownerName?.toLowerCase().includes(searchValue.toLowerCase()),
   );

   // Sort by relevance (exact matches first, then starts with, then contains)
   return filtered.sort((a, b) => {
      const aLabel = a.label.toLowerCase();
      const bLabel = b.label.toLowerCase();

      const aExact = aLabel === searchValue;
      const bExact = bLabel === searchValue;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      const aStarts = aLabel.startsWith(searchValue);
      const bStarts = bLabel.startsWith(searchValue);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;

      return aLabel.localeCompare(bLabel);
   });
};

const MotionAlertContent = motion(AlertDialogContent);

const AccountSelect = forwardRef(
   (
      {
         name,
         options,
         label,
         value,
         errors,
         isClearable = false,
         isSearchable = false,
         errorName,
         required = false,
         maxMenuHeight = 300,
         isMulti = false,
         virtualized = true,
         onChange,
         onKeyDown,
         showCustomNoOptions,
         openFrom,
         glGroup,
         ...props
      },
      ref,
   ) => {
      // State management
      const [isFocused, setIsFocused] = useState(false);
      const [inputValue, setInputValue] = useState('');
      const [menuIsOpen, setMenuIsOpen] = useState(false);
      const [isModalOpen, setIsModalOpen] = useState(false);
      const [isEditMode, setIsEditMode] = useState(false);
      const [currentRow, setCurrentRow] = useState({});
      const [demoname, setDemoname] = useState('');
      const [isOpen, setIsOpen] = useState(false);
      const { companyList } = useGlobal();
      const [mobile, setMobile] = useState('');
      const { user } = useUser();

      // New state for confirmation dialog
      const [isConfirmOpen, setIsConfirmOpen] = useState(false);
      const [pendingSearchValue, setPendingSearchValue] = useState('');
      const bgColor = useColorModeValue('white', 'gray.800');
      const headerColor = useColorModeValue('blue.600', 'blue.300');

      const gstDetail = useSelector((state) => state?.company?.gstDetail);
      const dispatch = useDispatch();
      // Refs
      const mobileInputRef = useRef(null);
      const saveBtnRef = useRef(null);
      const selectRef = useRef(null);
      const confirmCancelRef = useRef(null);
      const confirmYesRef = useRef(null);
      const { onOpen, onClose } = useDisclosure();

      // Theme values
      const bg = useColorModeValue('white', '#2d3748');
      const fontcolor = useColorModeValue('#9E9E9E', '#ffffff');
      const labelcolor = useColorModeValue('#000000', '#ffffff');

      // Memoized computed values
      const mappedOptions = useMemo(() => {
         if (!Array.isArray(options)) return [];

         const baseOptions = options.map((opt) => ({
            ...opt,
            value: opt.id,
            label: opt.label || opt.name || opt.id?.toString() || '',
         }));

         if (isMulti) {
            const allOptionValues = baseOptions.map((opt) => opt.value);
            const isAllSelected = value?.length === allOptionValues.length;
            const selectAllOption = {
               value: '*',
               label: isAllSelected ? 'Deselect All' : 'Select All',
            };
            return [selectAllOption, ...baseOptions];
         }

         return baseOptions;
      }, [options, isMulti, value]);

      // Filtered options based on search
      const filteredOptions = useMemo(() => {
         return filterOptions(mappedOptions, inputValue);
      }, [mappedOptions, inputValue]);

      // Check if no options available for current search
      const hasNoOptions = useMemo(() => {
         return inputValue && filteredOptions.length === 0;
      }, [inputValue, filteredOptions]);

      // Selected option(s)
      const selectedOption = useMemo(() => {
         if (isMulti) {
            if (!value || value.length === 0) return [];

            const allOptionValues = mappedOptions.filter((opt) => opt.value !== '*').map((opt) => opt.value);
            const isAllSelected = value.length === allOptionValues.length;

            if (isAllSelected) {
               return [
                  mappedOptions[0],
                  ...mappedOptions.filter((opt) => value.includes(opt.value) && opt.value !== '*'),
               ];
            }

            return mappedOptions.filter((opt) => value.includes(opt.value));
         }

         return mappedOptions.find((opt) => opt.value === value) || null;
      }, [mappedOptions, value, isMulti]);

      // Floating label logic
      const isFloating = isFocused || (value !== null && value !== undefined && value !== '');

      // Option selection handler
      const handleOptionSelect = useCallback(
         (option) => {
            if (isMulti) {
               const currentValue = value || [];

               if (option.value === '*') {
                  // Handle select/deselect all
                  const allValues = mappedOptions.filter((opt) => opt.value !== '*').map((opt) => opt.value);
                  const newValue = currentValue.length === allValues.length ? [] : allValues;
                  onChange(newValue);
               } else {
                  // Handle individual option toggle
                  const newValue = currentValue.includes(option.value)
                     ? currentValue.filter((v) => v !== option.value)
                     : [...currentValue, option.value];
                  onChange(newValue);
               }
            } else {
               onChange(option.value);
               setMenuIsOpen(false);
            }
         },
         [isMulti, value, onChange, mappedOptions],
      );

      // Handle opening AddEditForm for new account
      const handleOpenAddForm = useCallback((searchValue) => {
         setIsEditMode(false);
         setCurrentRow({});
         setDemoname(searchValue);
         setIsOpen(true);
      }, []);

      // Keyboard navigation handler
      const handleKeyDown = useCallback(
         (e) => {
            onKeyDown?.(e);

            const isCtrlEnter = e.ctrlKey && e.key === 'Enter';
            const isEnter = e.key === 'Enter' && !e.ctrlKey;
            const isTab = e.key === 'Tab';
            const isArrowDown = e.key === 'ArrowDown';
            const isArrowUp = e.key === 'ArrowUp';

            if (isCtrlEnter && !user.isExpired) {
               e.preventDefault();
               setIsEditMode(false);
               setCurrentRow({});
               setIsOpen(true);
               setDemoname(inputValue);
               return;
            }

            if (((e.ctrlKey && e.key === 'o') || e.key === 'F2') && !user.isExpired) {
               e.preventDefault();
               const focusedOption = selectRef.current?.state?.focusedOption;
               if (focusedOption) {
                  setIsEditMode(true);
                  setCurrentRow(focusedOption);
                  setIsOpen(true);
               }
               return;
            }

            if (isArrowDown || isArrowUp) {
               // Don't prevent default - let react-select handle the navigation
               return;
            }

            // Handle Enter key when no options are available
            if (isEnter && hasNoOptions && showCustomNoOptions && inputValue.trim() && !user.isExpired) {
               e.preventDefault();
               setPendingSearchValue(inputValue);
               setIsConfirmOpen(true);
               return;
            }

            if ((isEnter || isTab) && menuIsOpen) {
               const focusedOption = selectRef.current?.state?.focusedOption;
               if (focusedOption) {
                  handleOptionSelect(focusedOption);
                  e.preventDefault();

                  // Focus next element for Tab
                  if (isTab && props.nextRef?.current) {
                     setTimeout(() => {
                        props.nextRef.current.focus();
                     }, 0);
                  }
               }
            }

            if (isEnter && !menuIsOpen && props.nextRef?.current && !hasNoOptions) {
               setTimeout(() => {
                  props.nextRef.current.focus();
               }, 0);
            }
         },
         [
            onKeyDown,
            menuIsOpen,
            inputValue,
            props.nextRef,
            handleOptionSelect,
            hasNoOptions,
            showCustomNoOptions,
            handleOpenAddForm,
         ],
      );

      // Change handler
      const handleChange = useCallback(
         (selected, actionMeta) => {
            if (isMulti) {
               if (!selected || selected.length === 0) {
                  onChange([]);
                  return;
               }

               const isSelectAllClicked = selected.find((opt) => opt.value === '*');
               const allOptionValues = mappedOptions.filter((opt) => opt.value !== '*').map((opt) => opt.value);

               if (isSelectAllClicked && actionMeta.action === 'select-option') {
                  onChange(selected.length === mappedOptions.length ? [] : allOptionValues);
                  return;
               }

               if (actionMeta.action === 'deselect-option' && selected.length + 1 === mappedOptions.length) {
                  onChange([]);
                  return;
               }

               const values = selected.map((item) => item.value).filter((val) => val !== '*');
               onChange(values);
            } else {
               onChange(selected ? selected.value : '');
            }
         },
         [isMulti, onChange, mappedOptions],
      );

      // Handle save from modal
      const handleSave = useCallback(
         (formData) => {
            setMenuIsOpen(false);
            setIsFocused(false);
            onClose();
            const newOption = { ...formData, label: formData.name, value: formData.id };
            onChange(newOption.value);
            props.handleStockModalOnClick && props.handleStockModalOnClick(props.rowIndex);
            if (props.nextRef?.current) {
               setTimeout(() => {
                  props.nextRef.current.focus();
               }, 0);
            }
         },
         [onClose, onChange, props],
      );

      const handleClose = useCallback(() => {
         setIsOpen(false);
         setMenuIsOpen(false);
      }, []);

      // Handle confirmation dialog actions
      const handleConfirmYes = useCallback(() => {
         setIsConfirmOpen(false);
         handleOpenAddForm(pendingSearchValue);
      }, [pendingSearchValue, handleOpenAddForm]);

      const handleConfirmNo = useCallback(() => {
         setIsConfirmOpen(false);
         setPendingSearchValue('');
         // Focus moves to next element as per current behavior
         if (props.nextRef?.current) {
            setTimeout(() => {
               props.nextRef.current.focus();
            }, 0);
         }
      }, [props.nextRef]);

      // Imperative handle for ref
      useImperativeHandle(ref, () => ({
         focus: () => {
            if (selectRef.current) {
               selectRef.current.focus();
               if (required) {
                  setMenuIsOpen(true);
               }
            }
         },
      }));

      // Custom components based on configuration
      const customComponents = useMemo(() => {
         const components = {};

         components.Option = (props) => (
            <OptimizedOption
               {...props}
               isMulti={isMulti}
               selectProps={{
                  ...props.selectProps,
                  showBalance: ['salesdaybook', 'daybook'].includes(openFrom),
                  showEdit: showCustomNoOptions,
                  openFrom,
                  handleEdit: (option) => {
                     setIsEditMode(true);
                     setCurrentRow(option);
                     setIsOpen(true);
                  },
               }}
            />
         );

         // Use virtualization for large datasets
         if (virtualized && filteredOptions.length > 100) {
            components.MenuList = (props) => (
               <VirtualizedMenuList {...props} options={filteredOptions} maxHeight={maxMenuHeight} />
            );
         }

         if (showCustomNoOptions) {
            components.NoOptionsMessage = (props) => (
               <CustomNoOptionsMessage
                  {...props}
                  selectProps={{
                     ...props.selectProps,
                     showCustomNoOptions,
                     onOpenModal: () => setIsModalOpen(true),
                     onAddNew: (inputValue) => {
                        setCurrentRow({});
                        setIsEditMode(false);
                        setDemoname(inputValue);
                        setIsOpen(true);
                     },
                  }}
               />
            );
         }

         return components;
      }, [
         isMulti,
         showCustomNoOptions,
         mappedOptions.length,
         virtualized,
         filteredOptions,
         openFrom,
         maxMenuHeight,
         handleOpenAddForm,
      ]);

      const handleAddAccountByGST = useCallback(
         async (data) => {
            const response = await dispatch(addAccount(data));
            dispatch(removeDetails());
            if (response?.code === 1) {
               onChange(response?.data);
               data.name = '';
               data.gstRegNo = '';
               data.panNo = '';
               data.addressJson = [];
               data.mobile = '';
            }
            dispatch(removeDetails());
         },
         [dispatch, onChange],
      );

      useEffect(() => {
         if (isModalOpen && mobileInputRef.current) {
            mobileInputRef?.current?.focus();
         }
      }, [isModalOpen, mobileInputRef.current]);

      const inputcolor = useColorModeValue('#000000', '#ffffff');
      const bgcolor = useColorModeValue('#ffffff', '#101f4a');

      return (
         <>
            <FormControl isInvalid={errors && errors[errorName]} isRequired={required}>
               <Box position="relative" zIndex={2}>
                  <Select
                     {...props}
                     id={name}
                     ref={selectRef}
                     menuIsOpen={menuIsOpen}
                     onMenuOpen={() => {
                        setMenuIsOpen(true);
                        setIsFocused(true);
                     }}
                     onMenuClose={() => {
                        setMenuIsOpen(false);
                        setIsFocused(false);
                     }}
                     onKeyDown={handleKeyDown}
                     value={selectedOption}
                     onChange={handleChange}
                     onFocus={() => {
                        if (required) {
                           setMenuIsOpen(true);
                        }
                        setIsFocused(true);
                     }}
                     onBlur={() => {
                        setIsFocused(false);
                        setMenuIsOpen(false);
                     }}
                     options={filteredOptions}
                     isMulti={isMulti}
                     placeholder=""
                     isClearable
                     isSearchable
                     menuPlacement="auto"
                     onInputChange={setInputValue}
                     inputValue={inputValue}
                     closeMenuOnSelect={!isMulti}
                     hideSelectedOptions={false}
                     menuPortalTarget={document.body}
                     components={customComponents}
                     filterOption={() => true} // We handle filtering manually for better performance
                     styles={{
                        control: (base, state) => ({
                           ...base,
                           borderRadius: '6px',
                           width: '100%',
                           borderColor: errors[errorName] ? 'red' : state.isFocused ? 'blue' : '#CBD5E0',
                           boxShadow: errors[errorName]
                              ? '0 0 0 1px #e53e3e'
                              : state.isFocused
                              ? '0 0 0 1px #3182CE'
                              : 'none',
                           backgroundColor: bg,
                           color: fontcolor,
                           '&:hover': { borderColor: '#A0AEC0' },
                           minHeight: '40px',
                        }),
                        menu: (base) => ({
                           ...base,
                           zIndex: 9999,
                           position: 'absolute',
                           maxHeight: maxMenuHeight,
                           backgroundColor: bgcolor,
                        }),
                        option: (base, { isFocused, isSelected }) => ({
                           ...base,
                           backgroundColor: isSelected && !isMulti ? '#3182ce' : isFocused ? '#ebf8ff' : 'white',
                           color: isSelected && !isMulti ? 'white' : 'black',
                           cursor: 'pointer',
                           padding: '8px 12px',
                        }),
                        menuPortal: (base) => ({
                           ...base,
                           zIndex: 9999,
                        }),
                        valueContainer: (base) => ({
                           ...base,
                           padding: '2px 8px',
                           textColor: inputcolor,
                           color: inputcolor,
                        }),
                        input: (base) => ({
                           ...base,
                           margin: 0,
                           paddingBottom: 0,
                           paddingTop: 0,
                           color: inputcolor,
                        }),
                        singleValue: (baseStyles) => ({
                           ...baseStyles,
                           color: inputcolor,
                        }),
                     }}
                  />
                  {label && (
                     <FormLabel
                        htmlFor={name}
                        position="absolute"
                        top={isFloating ? '-8px' : '12px'}
                        left="12px"
                        fontSize={isFloating ? '12px' : '16px'}
                        // color="gray.500"
                        backgroundColor={bg}
                        color={labelcolor}
                        px="4px"
                        transition="all 0.2s ease-in-out"
                        pointerEvents="none">
                        {label}
                     </FormLabel>
                  )}
                  {errors[errorName] && <FormErrorMessage>{errors && errors[errorName]}</FormErrorMessage>}
               </Box>
            </FormControl>

            {/* GST Details Modal */}
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl" finalFocusRef={mobileInputRef}>
               <ModalOverlay />
               <ModalContent>
                  <ModalHeader>GST Details</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                     <Flex direction="column" gap={3}>
                        <Text>
                           <strong style={{ paddingRight: '10px' }}>Name:</strong> {gstDetail?.tradeNam}
                        </Text>
                        <Text>
                           <strong style={{ paddingRight: '10px' }}>Address:</strong>{' '}
                           {[
                              gstDetail?.pradr?.addr?.bno,
                              gstDetail?.pradr?.addr?.bnm,
                              gstDetail?.pradr?.addr?.st,
                              gstDetail?.pradr?.addr?.loc,
                              gstDetail?.pradr?.addr?.dst,
                              gstDetail?.pradr?.addr?.pncd,
                           ]
                              .filter(Boolean)
                              .join(', ')}
                        </Text>
                        <Input
                           ref={mobileInputRef}
                           placeholder="Enter Mobile"
                           value={mobile}
                           onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                           onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                 saveBtnRef.current.focus();
                              }
                           }}
                        />
                     </Flex>
                  </ModalBody>
                  <ModalFooter>
                     <Button
                        ref={saveBtnRef}
                        colorScheme="blue"
                        onClick={() => {
                           const addressData = [
                              gstDetail?.pradr?.addr?.bno || '',
                              gstDetail?.pradr?.addr?.bnm || '',
                              gstDetail?.pradr?.addr?.st || '',
                              gstDetail?.pradr?.addr?.loc || '',
                              gstDetail?.pradr?.addr?.dst || '',
                              gstDetail?.pradr?.addr?.pncd || '',
                           ]
                              .filter(Boolean)
                              .join(', ');
                           const data = {
                              name: gstDetail?.tradeNam,
                              addressJson: [{ name: 'billing', address1: addressData, mobile }],
                              gstRegNo: gstDetail?.gstin,
                              glGroupId: glGroup.id,
                              mobile,
                           };
                           handleAddAccountByGST(data);
                           setIsModalOpen(false);
                        }}
                        _focus={{ outlineColor: 'blue.500 ' }}>
                        Save
                     </Button>
                     <Button
                        colorScheme="red"
                        onClick={() => setIsModalOpen(false)}
                        _focus={{ outlineColor: 'blue.500 ' }}>
                        Cancel
                     </Button>
                  </ModalFooter>
               </ModalContent>
            </Modal>

            {/* Add Account Confirmation Dialog */}
            <AlertDialog
               isOpen={isConfirmOpen}
               leastDestructiveRef={confirmCancelRef}
               onClose={() => setIsConfirmOpen(false)}
               isCentered
               motionPreset="scale">
               <AlertDialogOverlay bg="blackAlpha.400" backdropFilter="blur(3px)" />

               <ScaleFade initialScale={0.8} in={isConfirmOpen}>
                  <MotionAlertContent
                     bg={bgColor}
                     borderRadius="2xl"
                     boxShadow="2xl"
                     p={2}
                     transition={{ duration: 0.25 }}>
                     <AlertDialogHeader
                        fontSize="xl"
                        fontWeight="bold"
                        color={headerColor}
                        display="flex"
                        alignItems="center"
                        gap={2}>
                        <Icon as={InfoIcon} boxSize={5} />
                        Add New Account
                     </AlertDialogHeader>

                     <AlertDialogBody>
                        <Text fontSize="md">
                           Do you want to add <b>"{pendingSearchValue}"</b> as a new account?
                        </Text>
                     </AlertDialogBody>

                     <AlertDialogFooter gap={3}>
                        <Button
                           ref={confirmCancelRef}
                           variant="outline"
                           colorScheme="gray"
                           leftIcon={<CloseIcon boxSize={3} />}
                           onClick={handleConfirmNo}
                           _focus={{ outlineColor: 'blue.500' }}
                           onKeyDown={(e) => {
                              if (e.key === 'ArrowRight') {
                                 e.preventDefault();
                                 confirmYesRef.current?.focus();
                              }
                           }}>
                           No
                        </Button>
                        <Button
                           ref={confirmYesRef}
                           colorScheme="blue"
                           leftIcon={<CheckCircleIcon boxSize={4} />}
                           onClick={handleConfirmYes}
                           _focus={{ outlineColor: 'blue.500' }}
                           onKeyDown={(e) => {
                              if (e.key === 'ArrowLeft') {
                                 e.preventDefault();
                                 confirmCancelRef.current?.focus();
                              }
                           }}>
                           Yes
                        </Button>
                     </AlertDialogFooter>
                  </MotionAlertContent>
               </ScaleFade>
            </AlertDialog>

            <AddEditForm
               isOpen={isOpen}
               onClose={() => setIsOpen(false)}
               isEditMode={isEditMode}
               currentRow={currentRow}
               initialData={currentRow}
               parentGlGroup={glGroup?.id}
               onSave={handleSave}
               demoname={isEditMode ? undefined : demoname}
               formType="Account"
               saveandcontinue={false}
            />
         </>
      );
   },
);

export default AccountSelect;
